import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface FinanceSummary {
  currentMonthRevenue: number
  currentMonthExpense: number
  balance: number
  totalRevenue: number
  totalExpense: number
  monthlyChart: Array<{ month: string; revenue: number; expense: number }>
  revenueByCategory: Array<{ category: string; total: number }>
  expenseByCategory: Array<{ category: string; total: number }>
}

export interface FinanceEntry {
  id: string
  type: 'revenue' | 'expense'
  category: string
  description: string | null
  amount: number
  date: string
  financePageId: string | null
  financePageName: string | null
  createdAt: number
}

export function useFinanceSummary() {
  return useQuery<FinanceSummary>({
    queryKey: ['finance', 'summary'],
    queryFn: () => api.get('/finance/summary'),
  })
}

export function useFinanceEntries(params?: { pageId?: string; month?: string; type?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.pageId) searchParams.set('page_id', params.pageId)
  if (params?.month) searchParams.set('month', params.month)
  if (params?.type) searchParams.set('type', params.type)
  const qs = searchParams.toString()

  return useQuery<FinanceEntry[]>({
    queryKey: ['finance', 'entries', params],
    queryFn: () => api.get(`/finance/entries${qs ? `?${qs}` : ''}`),
  })
}

export function useCreateEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      type: 'revenue' | 'expense'
      category: string
      description?: string
      amount: number
      date: string
      financePageId?: string | null
    }) => api.post<FinanceEntry>('/finance/entries', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })
}

export function useUpdateEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string
      type?: 'revenue' | 'expense'
      category?: string
      description?: string
      amount?: number
      date?: string
      financePageId?: string | null
    }) => api.put<FinanceEntry>(`/finance/entries/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })
}

export function useCustomCategories() {
  return useQuery<{ revenue: string[]; expense: string[] }>({
    queryKey: ['finance', 'categories'],
    queryFn: () => api.get('/finance/categories'),
  })
}

export function useDeleteEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/finance/entries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })
}
